import React, { useEffect, useState, useContext, useMemo } from "react";
import LayoutWrapper from "../../components/LayoutWrapper";
import HeaderBar from "../../components/HeaderBar";
import { FaBackward, FaSearch, FaPaperPlane } from "react-icons/fa";
import { ToastContainer, toast } from "react-toastify";
import { useNavigate, useLocation } from "react-router-dom";
import "react-toastify/dist/ReactToastify.css";
import axios from "axios";
import { ThemeContext } from "../../context/ThemeContext";
import getCurrentSkin from "../../utils/getCurrentSkin";

// ─── API base — all routes live under this prefix ────────────────────────────
const API = "https://nissi-india.com/api/stnInward";

const STNInward = () => {
  const { isDark, skin } = useContext(ThemeContext);
  const currentSkin = getCurrentSkin(skin);
  const navigate = useNavigate();
  const location = useLocation();

  // ── Session from localStorage ──────────────────────────────────────────────
  const finYearId = localStorage.getItem("finYearId") || "";
  const finYear = localStorage.getItem("finYear") || "";
  const branchId = localStorage.getItem("branchId") || "";
  const branch = localStorage.getItem("branchType") || "";
  const userId = localStorage.getItem("userId") || "";

  const commonHeaders = useMemo(
    () => ({
      "x-finyearid": finYearId,
      "x-finyear": finYear,
      "x-branchid": branchId,
      "x-branch": branch,
      "x-userid": userId,
    }),
    [finYearId, finYear, branchId, branch, userId]
  );

  const SESSION = useMemo(
    () => ({
      branchid: parseInt(branchId) || 0,
      loginuserid: parseInt(userId) || 0,
    }),
    [branchId, userId]
  );

  const { baseColor, baseShade } = currentSkin;
  const btnCls = `bg-${baseColor}-${baseShade} hover:bg-${baseColor}-700 text-white px-5 py-2 rounded transition-colors flex items-center gap-2 text-sm font-medium`;
  const inputCls =
    "w-full border border-gray-300 rounded px-2 py-1.5 text-sm focus:outline-none focus:ring-2 focus:ring-blue-300";
  const labelCls =
    "block text-xs font-semibold text-gray-600 mb-1 uppercase tracking-wide";
  const thCls = `bg-${baseColor}-${baseShade} text-white text-xs px-3 py-2 text-center border border-white/20`;
  const tdCls = "border border-gray-200 px-3 py-1.5 text-sm text-center";

  // ── Edit mode — passed from STNListings via navigate state ────────────────
  const editMode =
    location.state?.editMode || !!sessionStorage.getItem("stninwardid");

  const sessionInwardId =
    location.state?.stninwardid ||
    sessionStorage.getItem("stninwardid") ||
    null;

  const sessionInwardNo =
    location.state?.stninward ||
    sessionStorage.getItem("stninward") ||
    null;

  const sessionOutwardId =
    location.state?.stnoutwardid ||
    sessionStorage.getItem("stnoutwardid") ||
    null;

  // ── Form state ────────────────────────────────────────────────────────────
  const [date, setDate] = useState("");
  const [inwardType, setInwardType] = useState("0");
  const [receivedStore, setReceivedStore] = useState("");
  const [returnFrom, setReturnFrom] = useState("");
  const [remarks, setRemarks] = useState("");
  const [inwardNo, setInwardNo] = useState("");

  // ── Filter area state ─────────────────────────────────────────────────────
  const [branchCode, setBranchCode] = useState("Select");
  const [storeCode, setStoreCode] = useState("Select");
  const [stnOutwardNo, setStnOutwardNo] = useState("");

  // ── Dropdown lists ────────────────────────────────────────────────────────
  const [branchList, setBranchList] = useState([]);
  const [storeList, setStoreList] = useState([]);
  const [receivedStoreList, setReceivedStoreList] = useState([]);
  const [returnList, setReturnList] = useState([]);

  // ── Grid data ─────────────────────────────────────────────────────────────
  const [searchResults, setSearchResults] = useState([]);
  const [itemsGrid, setItemsGrid] = useState([]);
  const [updateGrid, setUpdateGrid] = useState([]);

  // ── Qty / selection state ─────────────────────────────────────────────────
  const [selectedOutwardRows, setSelectedOutwardRows] = useState({});
  const [selectedItemRows, setSelectedItemRows] = useState({});
  const [rejectedQtys, setRejectedQtys] = useState({});
  const [updateRejectedQtys, setUpdateRejectedQtys] = useState({});

  // ── Hidden field equivalents ──────────────────────────────────────────────
  const [hfstnoutid, setHfstnoutid] = useState("");
  const [hfStnRequestId, setHfStnRequestId] = useState("");

  // ── UI flags ──────────────────────────────────────────────────────────────
  const [showFilter, setShowFilter] = useState(true);
  const [showItemsGrid, setShowItemsGrid] = useState(false);
  const [showUpdateGrid, setShowUpdateGrid] = useState(false);

  useEffect(() => {
    loadDropdowns();

    if (editMode && sessionInwardId) {
      loadExistingInward();
    } else {
      setShowFilter(true);
      setShowItemsGrid(false);
      setShowUpdateGrid(false);
      generateInwardCode();
    }
  }, []);

  const loadDropdowns = async () => {
    try {
      const res = await axios.get(
        `${API}/stnInward_filldata?branchid=${SESSION.branchid}`,
        { headers: commonHeaders }
      );
      if (!res.data.success) {
        toast.error("Failed to load dropdown data");
        return;
      }
      const d = res.data.data || {};
      setBranchList(d.branches || []);
      setStoreList(d.storeCodes || []);
      setReceivedStoreList(d.receivedStores || []);
      setReturnList(d.returnFromBranches || []);
    } catch {
      toast.error("Failed to load dropdown data");
    }
  };

  const generateInwardCode = async () => {
    try {
      const res = await axios.get(
        `${API}/stnInward_generateCode?branchid=${SESSION.branchid}&userid=${SESSION.loginuserid}`,
        { headers: commonHeaders }
      );
      if (res.data.success) setInwardNo(res.data.code);
    } catch {}
  };

  const loadExistingInward = async () => {
    try {
      const detRes = await axios.get(
        `${API}/stnInward_getDets?inwardid=${sessionInwardId}`,
        { headers: commonHeaders }
      );
      const det = detRes.data.data?.[0];

      if (det) {
        const typeMap = { FI: "1", FO: "0", DO: "2" };
        const mappedType = typeMap[det.fld_fromreturnoroutward] || "0";
        setInwardType(mappedType);
        setInwardNo(det.fld_STN_InwardNo || sessionInwardNo || "");
        setDate(det.fld_STN_Date ? det.fld_STN_Date.substring(0, 10) : "");
        setRemarks(det.fld_Remarks || "");

        if (mappedType === "1") {
          const stRes = await axios.get(
            `${API}/stnInward_getStoreForInward?inwardid=${sessionInwardId}`,
            { headers: commonHeaders }
          );
          if (stRes.data.store) setReturnFrom(stRes.data.store);
        }

        if (mappedType === "0" || mappedType === "2") {
          const storeRes = await axios.get(
            `${API}/stnInward_getStoreId?inwardid=${sessionInwardId}`,
            { headers: commonHeaders }
          );
          if (storeRes.data.storeId) setReceivedStore(storeRes.data.storeId);
        }
      }

      const gridRes = await axios.get(
        `${API}/stnInward_getInwardDetails?branchid=${SESSION.branchid}&stnoutwardid=${sessionOutwardId}&stninwardid=${sessionInwardId}`,
        { headers: commonHeaders }
      );
      const items = gridRes.data.data || [];
      setUpdateGrid(items);

      const initRejected = {};
      items.forEach((it) => {
        initRejected[it.fld_STN_InwarddetID] = it.fld_RejectedQty || 0;
      });
      setUpdateRejectedQtys(initRejected);

      setShowFilter(false);
      setShowItemsGrid(false);
      setShowUpdateGrid(true);
    } catch {
      toast.error("Failed to load existing inward data");
    }
  };

  const handleInwardTypeChange = (val) => {
    setInwardType(val);
    setSearchResults([]);
    setItemsGrid([]);
    setShowItemsGrid(false);
    setSelectedOutwardRows({});
    setSelectedItemRows({});
    setStnOutwardNo("");
  };

  const handleSearch = async () => {
    if (!date) {
      toast.warning("Please Select Date");
      return;
    }
    if (inwardType === "0" && (!receivedStore || receivedStore === "Select")) {
      toast.warning("Please Select Received Store");
      return;
    }
    if (
      inwardType === "1" &&
      (!returnFrom || returnFrom === "" || returnFrom === "Select")
    ) {
      toast.warning("Please Select Return From Branch");
      return;
    }

    try {
      const res = await axios.post(
        `${API}/stnInward_searchOutward`,
        {
          outwardno: stnOutwardNo,
          inwardType,
          branchid: SESSION.branchid,
          ddl_branchcode: branchCode,
          ddl_storecode: storeCode,
          ddl_receivdstore: receivedStore,
          ddl_returnbranch: returnFrom,
        },
        { headers: commonHeaders }
      );

      if (res.data.success) {
        const data = res.data.data || [];
        setSearchResults(data);
        if (data.length === 0) toast.info("No records found");
      } else {
        toast.error(res.data.message || "Search failed");
      }
    } catch {
      toast.error("Search request failed. Please try again.");
    }
  };

  const handleSelectOutwardRow = async (row) => {
    if (!date) {
      toast.warning("Please Select Date");
      return;
    }

    setHfstnoutid(row.fld_STN_Outwardid);

    try {
      const reqRes = await axios.get(
        `${API}/stnInward_getRequestId?outwardid=${row.fld_STN_Outwardid}`,
        { headers: commonHeaders }
      );
      setHfStnRequestId(reqRes.data.requestId || "");
    } catch {}

    const type = inwardType === "1" ? "IW" : "OW";
    const endpoint = `${API}/stnInward_selectedItems?stn_inward_id=${row.fld_STN_Outwardid}&branchid=${SESSION.branchid}&type=${type}`;

    try {
      const itemRes = await axios.get(endpoint, { headers: commonHeaders });
      const items = itemRes.data.items || [];

      const initSelected = {};
      const initRejected = {};
      items.forEach((it) => {
        initSelected[it.fld_itemid] = inwardType === "1";
        initRejected[it.fld_itemid] = 0;
      });

      setItemsGrid(items);
      setSelectedItemRows(initSelected);
      setRejectedQtys(initRejected);
      setShowItemsGrid(true);
    } catch {
      toast.error("Failed to load items for selected outward");
    }
  };

  const handleSubmitNew = async () => {
    if (!date) {
      toast.warning("Please select Date");
      return;
    }
    if (!receivedStore || receivedStore === "Select") {
      toast.warning("Please select Received Store");
      return;
    }

    const filteredItems = itemsGrid.filter((it) => selectedItemRows[it.fld_itemid]);
    if (filteredItems.length === 0) {
      toast.warning("Please select at least one item");
      return;
    }

    const inwardTypeCode =
      inwardType === "0" ? "FO" : inwardType === "1" ? "FI" : "DO";

    const payload = {
      branchid: SESSION.branchid,
      userid: SESSION.loginuserid,
      stnid: parseInt(hfstnoutid),
      stnRequestId: parseInt(hfStnRequestId) || 0,
      inwardDate: `${date}T00:00:00Z`,
      remarks,
      inwardType: inwardTypeCode,
      storeId: parseInt(receivedStore),
      items: filteredItems.map((it) => ({
        itemId: it.fld_itemid,
        branchItemId: it.fld_branchitemid || 0,
        requestedQty: it.fld_RequestQty || 0,
        transferQty: it.fld_TransferQty || 0,
        rejectedQty: parseFloat(rejectedQtys[it.fld_itemid] || 0),
        actualQty:
          inwardType === "1"
            ? it.fld_TransferQty || 0
            : it.fld_actualQty || it.fld_TransferQty || 0,
        storeId: parseInt(receivedStore),
        stnRequestDetId: it.fld_stnrequestdetid || 0,
        selected: true,
        serialNumbers: [],
      })),
    };

    try {
      const res = await axios.post(`${API}/inward_submit`, payload, {
        headers: commonHeaders,
      });

      if (res.data.success || res.data.status === "Success") {
        toast.success("STN Inward submitted successfully!");
        setTimeout(() => navigate("/inventory/STNListing"), 1500);
      } else if (res.data.status === "2") {
        toast.error("STN already inwarded completely by another user");
      } else {
        toast.error(res.data.message || "Submission failed");
      }
    } catch {
      toast.error("Submission error. Please try again.");
    }
  };

  const handleSubmitUpdate = async () => {
    if (!date) {
      toast.warning("Please select Date");
      return;
    }

    const payload = {
      inwardDate: `${date}T00:00:00Z`,
      branchid: SESSION.branchid,
      userid: SESSION.loginuserid,
      remarks,
      inwardId: sessionInwardId,
      outwardId: hfstnoutid || sessionOutwardId,
      inwardType: inwardType === "0" ? "FO" : inwardType === "1" ? "FI" : "DO",
      storeId: parseInt(receivedStore),
      items: updateGrid.map((it) => ({
        stndetID: it.fld_STN_InwarddetID,
        itemId: it.fld_itemid,
        transferQty: parseFloat(it.fld_TransferQty || 0),
        actualQty: parseFloat(it.fld_actualQty || 0),
        rejectedQty: parseFloat(
          updateRejectedQtys[it.fld_STN_InwarddetID] ?? it.fld_RejectedQty ?? 0
        ),
      })),
    };

    try {
      const res = await axios.post(`${API}/stnInward_update`, payload, {
        headers: commonHeaders,
      });

      if (res.data.success) {
        toast.success("STN Inward updated successfully!");
        setTimeout(() => navigate("/inventory/STNListing"), 1500);
      } else {
        toast.error(res.data.message || "Update failed");
      }
    } catch {
      toast.error("Update error. Please try again.");
    }
  };
  // ═══════════════════════════════════════════════════════════════════════════
  // RENDER
  // ═══════════════════════════════════════════════════════════════════════════
  return (
    <LayoutWrapper>
      <HeaderBar title="STN INWARD - ENTRY" screenCode="A-26/2" />
      <ToastContainer position="top-right" autoClose={3000} />

      <div className={`p-6 mt-4 rounded-2xl shadow-xl border ${
        isDark
          ? "bg-white/10 backdrop-blur-md border-white/10 text-black"
          : `bg-white ${currentSkin.glass} text-black`
      }`}>

        {/* ── Top bar ── */}
        <div className="flex justify-between items-center mb-5">
          <button onClick={() => navigate(-1)} className={btnCls}>
            <FaBackward /> Back
          </button>
          {inwardNo && (
            <span className="text-xs font-mono bg-gray-100 border rounded px-3 py-1 text-gray-600">
              Inward No: <strong>{inwardNo}</strong>
            </span>
          )}
        </div>

        {/* ── Header form ── */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-6">

          {/* Left */}
          <div className="space-y-4">
            <div>
              <label className={labelCls}>Date <span className="text-red-500">*</span></label>
              <input
                type="date"
                value={date}
                onChange={(e) => setDate(e.target.value)}
                className={inputCls}
              />
            </div>

            <div>
              <label className={labelCls}>Inward Type <span className="text-red-500">*</span></label>
              <select
                value={inwardType}
                onChange={(e) => handleInwardTypeChange(e.target.value)}
                disabled={editMode}
                className={inputCls}
              >
                <option value="0">From Outward</option>
                <option value="1">From I/W Rejected Qty</option>
                <option value="2">From Direct Outward</option>
              </select>
            </div>

            <div>
              <label className={labelCls}>Remarks</label>
              <textarea
                value={remarks}
                onChange={(e) => setRemarks(e.target.value)}
                rows={2}
                className={inputCls}
              />
            </div>
          </div>

          {/* Right */}
          <div className="space-y-4">
            <div>
              <label className={labelCls}>Received Store <span className="text-red-500">*</span></label>
              <select
                value={receivedStore}
                onChange={(e) => setReceivedStore(e.target.value)}
                disabled={editMode}
                className={inputCls}
              >
                <option value="Select">-- Select --</option>
                {receivedStoreList.map((s, i) => (
                  <option key={i} value={s.fld_storeid}>{s.fld_storename}</option>
                ))}
              </select>
            </div>

            {/* Return From — FI only */}
            {inwardType === "1" && (
              <div>
                <label className={labelCls}>Return From <span className="text-red-500">*</span></label>
                <select
                  value={returnFrom}
                  onChange={(e) => setReturnFrom(e.target.value)}
                  disabled={editMode}
                  className={inputCls}
                >
                  <option value="">-- Select --</option>
                  {returnList.map((s, i) => (
                    <option key={i} value={s.fld_storeid}>{s.fld_storename}</option>
                  ))}
                </select>
              </div>
            )}
          </div>
        </div>

        {/* ── Filtration Area — add mode only ── */}
        {!editMode && showFilter && (
          <div className="border-t pt-5 mt-2">
            <h2 className="text-sm font-bold text-gray-500 uppercase tracking-widest mb-4">
              Filtration Area
            </h2>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-5">
              <div>
                <label className={labelCls}>Branch Code / Name</label>
                <select value={branchCode} onChange={(e) => setBranchCode(e.target.value)} className={inputCls}>
                  <option value="Select">-- Select --</option>
                  {branchList.map((b, i) => (
                    <option key={i} value={b.fld_branchid}>{b.fld_Branchname}</option>
                  ))}
                </select>
              </div>
              <div>
                <label className={labelCls}>Store Code / Name</label>
                <select value={storeCode} onChange={(e) => setStoreCode(e.target.value)} className={inputCls}>
                  <option value="Select">-- Select --</option>
                  {storeList.map((s, i) => (
                    <option key={i} value={s.fld_storeid}>{s.fld_storename}</option>
                  ))}
                </select>
              </div>
              <div>
                <label className={labelCls}>STN Outward No</label>
                <input
                  type="text"
                  value={stnOutwardNo}
                  onChange={(e) => setStnOutwardNo(e.target.value)}
                  placeholder="e.g. STNOUT/..."
                  className={inputCls}
                />
              </div>
            </div>
            <div className="flex justify-center mt-5">
              <button onClick={handleSearch} className={btnCls}>
                <FaSearch /> Search
              </button>
            </div>
          </div>
        )}

        {/* ── Search Results — gv_itemlist ── */}
        {!editMode && searchResults.length > 0 && (
          <div className="mt-6 overflow-x-auto">
            <h3 className="text-sm font-semibold text-gray-600 uppercase tracking-wide mb-2">
              Search Results
            </h3>
            <table className="w-full border text-sm">
              <thead>
                <tr>
                  {["S.No", "Outward No", "Outward Date", "From Branch", "To Store", "Select"].map(
                    (h) => <th key={h} className={thCls}>{h}</th>
                  )}
                </tr>
              </thead>
              <tbody>
                {searchResults.map((row, idx) => (
                  <tr
                    key={idx}
                    className={`cursor-pointer hover:bg-blue-50 ${
                      selectedOutwardRows[row.fld_STN_Outwardid] ? "bg-yellow-50" : ""
                    }`}
                    onClick={() => handleSelectOutwardRow(row)}
                  >
                    <td className={tdCls}>{idx + 1}</td>
                    <td className={`${tdCls} text-blue-600 underline font-medium`}>
                      {row.fld_STN_OutwardNo}
                    </td>
                    <td className={tdCls}>{row.fld_STN_Date?.substring(0, 10)}</td>
                    <td className={tdCls}>{row.fld_branchname}</td>
                    <td className={tdCls}>{row.fld_Storename}</td>
                    <td className={tdCls}>
                      <input
                        type="checkbox"
                        checked={!!selectedOutwardRows[row.fld_STN_Outwardid]}
                        onChange={(e) => {
                          e.stopPropagation();
                          setSelectedOutwardRows((prev) => ({
                            ...prev,
                            [row.fld_STN_Outwardid]: e.target.checked,
                          }));
                          if (e.target.checked) handleSelectOutwardRow(row);
                        }}
                      />
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}

        {/* ── Items Grid — gv_branchitemmaster (add mode) ── */}
        {!editMode && showItemsGrid && itemsGrid.length > 0 && (
          <div className="mt-6 overflow-x-auto">
            <h3 className="text-sm font-semibold text-gray-600 uppercase tracking-wide mb-2">
              Item Details
            </h3>
            <table className="w-full border text-sm">
              <thead>
                <tr>
                  {[
                    "S.No", "Item Code", "Item Name", "UOM",
                    inwardType === "1" ? "Inwarded Qty" : "Transfer Qty",
                    ...(inwardType !== "1" ? ["Rejected Qty"] : []),
                    "Select",
                  ].map((h) => <th key={h} className={thCls}>{h}</th>)}
                </tr>
              </thead>
              <tbody>
                {itemsGrid.map((item, idx) => (
                  <tr key={idx} className="hover:bg-gray-50">
                    <td className={tdCls}>{idx + 1}</td>
                    <td className={tdCls}>{item.fld_itemcode}</td>
                    <td className={tdCls}>{item.fld_itemname}</td>
                    <td className={tdCls}>{item.fld_uomname}</td>
                    <td className={tdCls}>{item.fld_TransferQty}</td>

                    {inwardType !== "1" && (
                      <td className={tdCls}>
                        <input
                          type="number"
                          min={0}
                          max={item.fld_TransferQty || 0}
                          value={rejectedQtys[item.fld_itemid] ?? 0}
                          onChange={(e) => {
                            const val = parseFloat(e.target.value) || 0;
                            if (val > (item.fld_TransferQty || 0)) {
                              toast.warning("Rejected Qty cannot exceed Transfer Qty");
                              return;
                            }
                            setRejectedQtys((prev) => ({ ...prev, [item.fld_itemid]: val }));
                          }}
                          className="w-20 border rounded px-2 py-0.5 text-center text-sm"
                        />
                      </td>
                    )}

                    <td className={tdCls}>
                      <input
                        type="checkbox"
                        checked={!!selectedItemRows[item.fld_itemid]}
                        onChange={(e) =>
                          setSelectedItemRows((prev) => ({
                            ...prev,
                            [item.fld_itemid]: e.target.checked,
                          }))
                        }
                      />
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
            <div className="flex justify-center mt-4">
              <button onClick={handleSubmitNew} className={btnCls}>
                <FaPaperPlane /> Submit Inward
              </button>
            </div>
          </div>
        )}

        {/* ── Update Grid — gv_updateinward (edit mode) ── */}
        {editMode && showUpdateGrid && updateGrid.length > 0 && (
          <div className="mt-6 overflow-x-auto">
            <h3 className="text-sm font-semibold text-gray-600 uppercase tracking-wide mb-2">
              Inward Items (Edit)
            </h3>
            <table className="w-full border text-sm">
              <thead>
                <tr>
                  {[
                    "S.No", "Item Code", "Item Name", "UOM",
                    "Transfer Qty", "Inwarded Qty",
                    ...(inwardType !== "1" ? ["Rejected Qty"] : []),
                  ].map((h) => <th key={h} className={thCls}>{h}</th>)}
                </tr>
              </thead>
              <tbody>
                {updateGrid.map((item, idx) => (
                  <tr key={idx} className="hover:bg-gray-50">
                    <td className={tdCls}>{idx + 1}</td>
                    <td className={tdCls}>{item.fld_itemcode}</td>
                    <td className={tdCls}>{item.fld_itemname}</td>
                    <td className={tdCls}>{item.fld_uomname}</td>
                    <td className={tdCls}>{item.fld_actualQty}</td>
                    <td className={tdCls}>{item.fld_TransferQty}</td>

                    {inwardType !== "1" && (
                      <td className={tdCls}>
                        <input
                          type="number"
                          min={0}
                          max={item.fld_TransferQty || 0}
                          value={
                            updateRejectedQtys[item.fld_STN_InwarddetID] ??
                            item.fld_RejectedQty ?? 0
                          }
                          onChange={(e) => {
                            const val = parseFloat(e.target.value) || 0;
                            if (val > (item.fld_TransferQty || 0)) {
                              toast.warning("Rejected Qty should not exceed Transfer Qty");
                              return;
                            }
                            setUpdateRejectedQtys((prev) => ({
                              ...prev,
                              [item.fld_STN_InwarddetID]: val,
                            }));
                          }}
                          className="w-20 border rounded px-2 py-0.5 text-center text-sm"
                        />
                      </td>
                    )}
                  </tr>
                ))}
              </tbody>
            </table>
            <div className="flex justify-center mt-4">
              <button onClick={handleSubmitUpdate} className={btnCls}>
                <FaPaperPlane /> Update Inward
              </button>
            </div>
          </div>
        )}

      </div>
    </LayoutWrapper>
  );
};

export default STNInward;